/**
 * UI Stitch — onboarding self-service (proyecto 16632136914967751961).
 * Composición y tokens del MCP; lógica de negocio en ContratarModal.
 */
import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  ArrowLeft, ArrowRight, Briefcase, Building2, CheckCircle2, ChevronDown,
  Clock, CreditCard, Eye, EyeOff, Info, Loader2, Wallet, X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPlanArs } from "@/lib/pricing-catalog";

export type OnboardingStepIndex = 1 | 2 | 3 | 4;

const C = {
  bg: "var(--background)",
  surfaceLow: "var(--card)",
  surfaceLowest: "var(--background)",
  surfaceHigh: "var(--muted)",
  surfaceHighest: "var(--muted)",
  mint: "var(--primary)",
  mintContainer: "var(--primary-hover)",
  onSurface: "var(--foreground)",
  onSurfaceVariant: "var(--muted-foreground)",
  outline: "var(--border)",
  onPrimary: "var(--primary-foreground)",
} as const;

export function OnboardingProgress({ step }: { step: OnboardingStepIndex }) {
  return (
    <div className="flex items-center gap-2 px-1">
      {([1, 2, 3, 4] as const).map((n) => (
        <div
          key={n}
          className={cn(
            "h-1.5 flex-1 rounded-full transition-colors",
            n <= step ? "bg-primary" : "bg-muted",
            n === step && step === 3 && "shadow-[0_0_15px_rgba(93,218,202,0.4)]",
          )}
        />
      ))}
    </div>
  );
}

export function OnboardingStitchModal({
  open,
  onOpenChange,
  brandName,
  children,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  brandName: string;
  children: React.ReactNode;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pointer-events-none">
          <DialogPrimitive.Content
            className={cn(
              "pointer-events-auto w-full max-w-[600px] max-h-[90vh] overflow-y-auto rounded-xl border shadow-2xl",
              "border-border bg-card text-foreground",
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            )}
            style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            aria-describedby="onboarding-modal-desc"
          >
            <DialogPrimitive.Title className="sr-only">
              Contratar {brandName}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description id="onboarding-modal-desc" className="sr-only">
              Completá los pasos para activar tu plan: datos del negocio, cuenta y pago.
            </DialogPrimitive.Description>
            <div className="sticky top-0 z-10 flex h-14 items-center justify-between border-b border-border/60 bg-card/95 px-6 backdrop-blur-sm">
              <span className="text-lg font-bold text-primary">{brandName}</span>
              <div className="flex items-center gap-3">
                <DialogPrimitive.Close className="rounded-full p-1 text-muted-foreground transition-colors hover:text-primary">
                  <X className="h-5 w-5" />
                  <span className="sr-only">Cerrar</span>
                </DialogPrimitive.Close>
              </div>
            </div>
            {children}
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function OnboardingStepHeader({
  planName,
  subtitle,
  usdTotal,
  arsTotal,
  period,
  centered,
}: {
  planName: string;
  subtitle: string;
  /** @deprecated Prefer arsTotal */
  usdTotal?: number;
  arsTotal?: number;
  period: string;
  centered?: boolean;
}) {
  const total = arsTotal ?? usdTotal ?? 0;
  return (
    <div className={cn("mb-8 gap-4", centered ? "text-center" : "flex flex-col sm:flex-row sm:items-end sm:justify-between")}>
      <div>
        <p className="text-sm font-medium text-primary">Contratar plan</p>
        <h1 className="mt-1 text-2xl font-bold text-foreground sm:text-[32px] sm:leading-tight">
          {planName}
        </h1>
        <p className="mt-2 text-base text-muted-foreground">{subtitle}</p>
      </div>
      {!centered && (
        <div className="shrink-0 rounded-lg border border-primary/30 bg-primary/20 px-4 py-2">
          <span className="text-xl font-semibold tabular-nums text-primary whitespace-nowrap">
            {formatPlanArs(total)}
            <span className="text-sm font-normal text-muted-foreground">{period}</span>
          </span>
        </div>
      )}
    </div>
  );
}

export function StitchLabel({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-2 ml-1 block text-sm font-semibold text-muted-foreground">
      {children}
    </label>
  );
}

export function StitchInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        "w-full rounded-xl border border-border bg-background px-4 py-4 text-base text-foreground placeholder:text-muted-foreground",
        "transition-all focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary focus:shadow-[0_0_15px_rgba(93,218,202,0.2)]",
        props.className,
      )}
    />
  );
}

export function StitchSelect(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select
        {...props}
        className={cn(
          "w-full appearance-none rounded-xl border border-border bg-background px-4 py-4 pr-10 text-base text-foreground",
          "focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary",
          props.className,
        )}
      />
      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
    </div>
  );
}

export function StitchInfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-4 rounded-xl border border-border bg-muted p-6">
      <Info className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
      <p className="text-sm leading-relaxed text-muted-foreground">{children}</p>
    </div>
  );
}

export function StitchBtnPrimary({
  children,
  className,
  loading,
  type = "button",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }) {
  return (
    <button
      type={type}
      {...props}
      disabled={props.disabled || loading}
      className={cn(
        "flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-4 text-base font-semibold text-primary-foreground",
        "shadow-[0_0_20px_rgba(93,218,202,0.3)] transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50",
        className,
      )}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

export function StitchBtnOutline({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        "rounded-xl border border-primary px-8 py-4 text-sm font-semibold text-primary transition-all hover:bg-primary/10 active:scale-95",
        className,
      )}
    >
      {children}
    </button>
  );
}

export function StitchBtnGhostLink({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      className={cn("w-full py-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground", className)}
    >
      {children}
    </button>
  );
}

export function StitchPasswordInput({
  value,
  onChange,
  id,
  placeholder = "••••••••",
  required,
}: {
  value: string;
  onChange: (v: string) => void;
  id?: string;
  placeholder?: string;
  required?: boolean;
}) {
  const [show, setShow] = React.useState(false);
  return (
    <div className="relative">
      <StitchInput
        id={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="pr-12"
      />
      <button
        type="button"
        className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
        onClick={() => setShow((s) => !s)}
        tabIndex={-1}
      >
        {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
      </button>
    </div>
  );
}

export function OnboardingSuccessPanel({
  isManual,
  isPayway,
  businessName,
  onClose,
}: {
  isManual: boolean;
  isPayway: boolean;
  businessName: string;
  onClose: () => void;
}) {
  const title = isManual ? "¡Pedido recibido!" : isPayway ? "¡Cuenta activada!" : "¡Activando tu cuenta!";
  return (
    <div className="relative overflow-hidden px-6 py-10 sm:px-12 sm:py-12">
      <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-primary/10 blur-[80px]" />
      <div className="relative z-10 flex flex-col items-center text-center">
        <OnboardingProgress step={4} />
        <div className="mb-8 mt-10 flex h-24 w-24 animate-in zoom-in items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_0_40px_-10px_rgba(93,218,202,0.4)] sm:h-32 sm:w-32">
          <CheckCircle2 className="h-12 w-12 sm:h-16 sm:w-16" strokeWidth={2} />
        </div>
        <h1 className="mb-6 text-3xl font-bold tracking-tight text-white sm:text-4xl">{title}</h1>
        <div className="max-w-md space-y-6">
          {isManual ? (
            <p className="text-lg leading-relaxed text-muted-foreground">
              Enviá el comprobante de transferencia <span className="font-bold text-primary">ARS</span> a{" "}
              <a className="underline hover:text-primary" href="mailto:billing@structura.com.ar">billing@structura.com.ar</a>{" "}
              indicando tu negocio &quot;{businessName}&quot;.
            </p>
          ) : (
            <p className="text-lg leading-relaxed text-muted-foreground">
              Tu pago fue confirmado. Revisá tu email para acceder al panel.
            </p>
          )}
          {isManual && (
            <div className="inline-flex items-center gap-3 rounded-full border border-border bg-muted px-6 py-3">
              <Clock className="h-5 w-5 text-primary" />
              <p className="text-sm font-semibold uppercase tracking-wider text-foreground">Activamos en menos de 24hs hábiles</p>
            </div>
          )}
        </div>
        <div className="mt-12 w-full space-y-4">
          <StitchBtnPrimary onClick={onClose}>Ir al dashboard</StitchBtnPrimary>
        </div>
      </div>
    </div>
  );
}

export const stitchIcons = {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Building2,
  CreditCard,
  Wallet,
};

export { C as stitchColors };
